<template>
    <div class="h-screen bg-surface-0 dark:bg-surface-950 flex flex-col">
        <div
            class="myheader bg-gray-100 dark:bg-gray-800 flex flex-row flex-wrap items-center justify-between py-4 px-4 gap-y-2"
        >
            <div class="flex items-center gap-3">
                <img :src="isDarkMode ? dark_logo : logo" class="h-[2rem] cursor-pointer" @click="handleDashBoard" />
            </div>

            <div
                class="flex-1 px-4 rounded-b-xl lg:rounded-none top-full right-0 w-full bg-surface-0 dark:bg-surface-950 hidden md:flex flex-row items-center transition-all"
            >
                <div class="flex justify-center flex-row flex-1">
                    <IconField class="w-3/4">
                        <InputIcon class="pi pi-search" />
                        <InputText class="w-full" @keyup="handleSearch" placeholder="Search" />
                    </IconField>
                </div>

                <div class="flex flex-row items-center">
                    <div class="flex flex-row gap-2 lg:items-center w-full">
                        <div v-if="hasCreate && !hasBulk" class="flex">
                            <Button label="Create" icon="pi pi-plus" @click="openCreateTestimonial" />
                        </div>
                        <div v-else-if="hasBulk && !hasCreate" class="flex">
                            <Button label="Bulk Upload" icon="pi pi-cloud-upload" @click="openBulkUploadTestimonial" />
                        </div>
                        <div v-else-if="hasCreate && hasBulk" class="flex">
                            <SplitButton
                                label="Create"
                                icon="pi pi-plus"
                                :model="splitButtonItems"
                                @click="openCreateTestimonial"
                            />
                        </div>

                        <Button
                            v-if="authService.hasPermission(PermissionsEnum.viewSettings)"
                            outlined
                            severity="secondary"
                            class="!rounded-lg justify-center w-9 h-9"
                            @click="goToSettings"
                        >
                            <i class="pi pi-cog !text-xl !leading-none" />
                        </Button>

                        <Button
                            outlined
                            severity="secondary"
                            class="!rounded-lg justify-center w-9 h-9"
                            @click="toggleDark"
                        >
                            <i :class="['pi !text-xl !leading-none', isDarkMode ? 'pi-moon' : 'pi-sun']" />
                        </Button>
                    </div>
                </div>
            </div>
            <div
                class="flex items-center xl:pr-8 cursor-pointer"
                aria-haspopup="true"
                aria-controls="overlay_menu"
                @click="toggle"
            >
                <img class="!w-10 xl:!w-9 !h-10 xl:!h-9 rounded-full" :src="authService.currentUser.imgUrl" alt="" />
                <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" />
            </div>

            <div class="md:hidden sm:flex justify-center flex-row w-screen">
                <IconField class="w-full">
                    <InputIcon class="pi pi-search" />
                    <InputText class="w-full" @keyup.enter="handleSearch" placeholder="Search" />
                </IconField>
            </div>
        </div>
        <div class="bg-white dark:bg-surface-950 h-full flex flex-col flex-1 overflow-hidden">
            <router-view />
        </div>

        <div
            class="bg-gray-100 dark:bg-gray-800 md:hidden flex justify-evenly items-center flex-row w-screen py-2 buttonBox"
        >
            <Button icon="pi pi-home" variant="text" severity="contrast" size="large" @click="handleDashBoard" />

            <Button icon="pi pi-plus" rounded severity="contrast" size="large" @click="openCreateTestimonial" />

            <Button icon="pi pi-cog" variant="text" severity="contrast" size="large" @click="goToSettings" />
        </div>
    </div>

    <TestimonialDialog
        v-if="showTDialog"
        v-model:visible="showTDialog"
        :testimonialId="testimonialId"
        @testimonialUpdated="onRefresh"
    >
    </TestimonialDialog>

    <TestimonialBulkUploadDialog v-if="showBulkDialog" v-model:visible="showBulkDialog"> </TestimonialBulkUploadDialog>
</template>
<script setup>
import Button from "primevue/button";
import { ref, onMounted, onUnmounted, inject } from "vue";
import { useRouter } from "vue-router";
import { PermissionsEnum } from "@/core/enums/PermissionsEnum";
import { Events } from "@/core/enums/EventsEnum";
import { InputText, InputIcon, IconField } from "primevue";
import TestimonialDialog from "../testimonials/TestimonialDialog.vue";
import { Menu } from "primevue";
import emitter from "@/boot/eventBus";
import { useToast } from "primevue/usetoast";
import logo from "@/assets/testimonial_logo.png";
import dark_logo from "@/assets/logo_dark_mode.png";
import TestimonialBulkUploadDialog from "../testimonials/TestimonialBulkUploadDialog.vue";
import SplitButton from "primevue/splitbutton";
import { computed } from "vue";
import debounce from "lodash/debounce";

const authService = inject("authService");
const router = useRouter();

const showTDialog = ref(false);
const isDarkMode = ref(false);
const testimonialId = ref(null);
const showBulkDialog = ref(false);

const toast = useToast();

const handleDashBoard = async () => {
    if (router.currentRoute.value.name != "") {
        await router.replace({ path: "/" });
    }

    emitter.emit(Events.testimonialSearch, {});
};

const navs = ref([
    {
        icon: "pi pi-file !text-xl !leading-none",
        label: "Testimonials",
        action: handleDashBoard,
    },
]);

const menu = ref();
const items = ref([
    {
        label: authService.getUserName(),
        items: [
            {
                label: "Log-out",
                icon: "pi pi-refresh",
                command: () => {
                    authService.removeToken();
                    router.replace({ path: "/login" });
                },
            },
        ],
    },
]);

const toggle = (event) => {
    menu.value.toggle(event);
};

const goToSettings = () => {
    router.push({ path: "/settings" });
};

const handleSearch = debounce(async (event) => {
    const searchTerm = event.target.value;

    if (router.currentRoute.value.name != "") {
        await router.replace({ path: "/" });
    }

    if (searchTerm != "") {
        emitter.emit(Events.aiTestimonialSearch, { searchTerm });
        return;
    }

    emitter.emit(Events.testimonialSearch, {});
}, 800);

const showToast = (data) => {
    toast.add({
        severity: "error",
        summary: "Error",
        detail: data.message,
        life: 4000,
    });
};

const hasCreate = computed(() => authService.hasPermission(PermissionsEnum.newTestimonial));

const hasBulk = computed(() => authService.hasPermission(PermissionsEnum.bulkUploadTestimonials));

const splitButtonItems = computed(() => {
    const items = [];

    if (hasBulk.value) {
        items.push({
            label: "Bulk Upload",
            icon: "pi pi-cloud-upload",
            command: openBulkUploadTestimonial,
        });
    }
    return items;
});

const openCreateTestimonial = () => {
    testimonialId.value = null;
    showTDialog.value = true;
};

const openBulkUploadTestimonial = () => {
    testimonialId.value = null;
    showBulkDialog.value = true;
};

const openEditTestimonial = async (data) => {
    testimonialId.value = data.id;
    showTDialog.value = true;
};

const onRefresh = async (updatedId) => {
    emitter.emit(Events.testimonialUpdated, { id: updatedId });
};

onMounted(() => {
    emitter.emit(Events.testimonialSearch, {});
    emitter.on(Events.errorToast, showToast);
    emitter.on(Events.editTestimonial, openEditTestimonial);
});

onUnmounted(() => {
    emitter.off(Events.errorToast);
    emitter.off(Events.editTestimonial);
});

const toggleDark = () => {
    authService.updateSettings({ darkMode: !authService.isDarkMode() });

    // fix the dark mode saving
    document.documentElement.classList.toggle("my-app-dark");
    isDarkMode.value = authService.isDarkMode();
};
</script>

<style>
.myheader {
    position: sticky;
    top: 0;
    z-index: 10;
}

.buttonBox {
    box-shadow: 0px 1px 14px 4px rgba(0, 0, 0, 0.6);
}
</style>
